import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "@/contexts/AuthContext";
import { useDevBypassRedirect } from "@/hooks/use-dev-bypass-redirect";
import { NanaeLogoSymbol } from "@/components/qv3/NanaeLogo";
import { socialMeta } from "@/lib/seo-meta";

const schema = z
  .object({
    password: z.string().min(8, "8 caractères minimum"),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, {
    message: "Les mots de passe ne correspondent pas",
    path: ["confirm"],
  });
type Values = z.infer<typeof schema>;

export const Route = createFileRoute("/reset-password")({
  head: () => ({
    meta: [{ title: "Nouveau mot de passe — Hōjin" }, { name: "robots", content: "noindex, nofollow" },
      ...socialMeta({
        title: "Nouveau mot de passe — Hōjin",
        description: "Nouveau mot de passe — Hōjin",
        url: "https://hojin.ai/reset-password",
        image: "conseil",
        card: "summary_large_image",
      }),
    ],
    links: [{ rel: "canonical", href: "https://hojin.ai/reset-password" }],
  }),
  component: ResetPasswordPage,
});

export function ResetPasswordPage() {
  useDevBypassRedirect();
  const { updatePassword } = useAuth();
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await updatePassword(values.password);
      navigate({ to: "/foyer" });
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "Mise à jour impossible");
    }
  });

  return (
    <div className="nanae-auth-shell qv3 qv3-dark">
      <main className="nanae-auth-main">
        <section className="nanae-auth-card" aria-labelledby="reset-title">
          <span className="nanae-auth-card-corner left">N°SÉSAME · MMXXVI</span>
          {formError && (
            <span className="nanae-auth-card-corner right">
              <span className="qv3-led" aria-hidden="true" />
              ERR
            </span>
          )}

          <div className="nanae-auth-logo">
            <NanaeLogoSymbol size="xl" />
          </div>

          <p className="nanae-auth-eyebrow">§ NOUVELLES CLEFS</p>
          <h1 id="reset-title" className="nanae-auth-title">
            Choisissez un nouveau sésame.
          </h1>
          <p className="nanae-auth-subtitle">Pour que le Conseil ne s'ouvre qu'à vous.</p>

          <form onSubmit={onSubmit} noValidate style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <div className="qv3-field">
              <label className="qv3-field-label" htmlFor="reset-password">NOUVEAU MOT DE PASSE</label>
              <input
                id="reset-password"
                type="password"
                autoComplete="new-password"
                className="qv3-input-lcd"
                aria-invalid={errors.password ? "true" : "false"}
                placeholder="••••••••"
                {...register("password")}
              />
              {errors.password && <p className="qv3-field-error">— {errors.password.message}</p>}
            </div>

            <div className="qv3-field">
              <label className="qv3-field-label" htmlFor="reset-confirm">CONFIRMATION</label>
              <input
                id="reset-confirm"
                type="password"
                autoComplete="new-password"
                className="qv3-input-lcd"
                aria-invalid={errors.confirm ? "true" : "false"}
                placeholder="••••••••"
                {...register("confirm")}
              />
              {errors.confirm && <p className="qv3-field-error">— {errors.confirm.message}</p>}
            </div>

            {formError && (
              <p role="alert" className="nanae-auth-error">
                <span className="qv3-led" aria-hidden="true" />
                {formError}
              </p>
            )}

            <button type="submit" className="qv3-btn-hifi" disabled={isSubmitting}>
              {isSubmitting ? "Mise à jour…" : "Confirmer"}
              {!isSubmitting && <span className="qv3-btn-hifi-hint">ENTER →</span>}
            </button>
          </form>

          <div className="nanae-auth-altlinks">
            <Link to="/login" className="nanae-auth-altlink">
              <strong>Retour à l'accès</strong>
            </Link>
          </div>
        </section>
      </main>
      <footer className="nanae-auth-foot" data-rp="auth-footer">SEPT VOIX, UNE DIRECTION · MMXXVI</footer>
    </div>
  );
}
